import { zodResolver } from "@hookform/resolvers/zod";
import { getLegalNameAnnotation } from "features/commitments/CommitmentUtils";
import { useSecurityClassListSuspense } from "gen/endpoints/fund-security-classes/fund-security-classes";
import { useInvestorClassListSuspense } from "gen/endpoints/investor-class/investor-class";
import {
  useSecurityOwnershipRetrieve,
  useSecurityOwnershipUpdate,
} from "gen/endpoints/security-ownership/security-ownership";
import { SecurityOwnershipReadData } from "gen/model/securityOwnershipReadData";
import { securityOwnershipUpdateBody } from "gen/schemas/security-ownership/security-ownership";
import { cloneDeep } from "lodash-es";
import React, { useCallback } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormProps } from "shared/components/form/Form";
import { useFormDrawerInitialFocusRef } from "shared/components/form/FormDrawer";
import { FormSelect } from "shared/components/input/select/Select";
import { SelectOption } from "shared/components/input/select/SelectOption";
import { FormMoneyInput } from "shared/components/input/text/MoneyInput";

interface CommitmentEditFormProps {
  fundFamilyId: string;
  commitmentId: string;
}

export const useCommitmentEditForm = ({
  fundFamilyId,
  commitmentId,
}: CommitmentEditFormProps): FormProps<SecurityOwnershipReadData, void> => {
  const { data: commitmentData } = useSecurityOwnershipRetrieve(
    fundFamilyId,
    commitmentId,
    {
      query: {
        // react-hook-form mutates `data` object, which is frozen, so we need to clone it
        // this is needed in order to use `values` instead of `defaultValues` in useForm
        // which in turn is needed because this is a non-suspending query so data is
        // not available immediately.
        select: (data) => cloneDeep(data),
      },
    },
  );

  const { mutateAsync: updateCommitment } = useSecurityOwnershipUpdate();

  const form = useForm({
    resolver: zodResolver(securityOwnershipUpdateBody),
    values: commitmentData,
  });

  const onSubmit = useCallback(
    async (data: SecurityOwnershipReadData) => {
      await updateCommitment({
        fundFamilyId,
        id: commitmentId,
        data,
      });
    },
    [commitmentId, updateCommitment, fundFamilyId],
  );

  return { form, onSubmit };
};

export interface CommitmentFormFieldsProps {
  fundFamilyId: string;
}

export const CommitmentFormFields: React.FC<CommitmentFormFieldsProps> = ({
  fundFamilyId,
}) => {
  const { t } = useTranslation(["commitment", "common"]);

  const { data: fundSecurityData } = useSecurityClassListSuspense(fundFamilyId);
  const { data: investorClassData } =
    useInvestorClassListSuspense(fundFamilyId);

  const initialFocusRef = useFormDrawerInitialFocusRef();

  return (
    <>
      <FormMoneyInput
        name="commitment_amount"
        title={t("commitment:commitmentAmount")}
        getInputRef={initialFocusRef}
        autoFocus={true}
      />
      <FormSelect name="security_class_id" title={t("common:fund")}>
        {fundSecurityData.map((securityClass) => (
          <SelectOption key={securityClass.id} value={securityClass.id}>
            {getLegalNameAnnotation(securityClass)}
          </SelectOption>
        ))}
      </FormSelect>
      <FormSelect name="investor_class_id" title={t("common:commitmentClass")}>
        {investorClassData.map((investorClass) => (
          <SelectOption key={investorClass.id} value={investorClass.id}>
            {investorClass.name}
          </SelectOption>
        ))}
      </FormSelect>
    </>
  );
};
